/**
 * Days left in a trial, counted in CALENDAR days (2026-09-26).
 *
 * The Seven Free Days clock ends at the last second of the 7th local day after
 * the start day (services/trial_clock.py compute_trial_expiry). Counted in
 * 24-hour units, `Math.ceil((expires - now) / 1 day)` on the start day is
 * ceil(7 days + the rest of today) = 8: Account read "Trial • 8 days left" on
 * day 0 of a 7-day week. Counted in calendar days — the end date minus today's
 * date, both in the phone's local time — the start day reads 7, the day before
 * the last reads 1, and the last day reads 0 ("last day").
 *
 * A store trial (a 7 × 24 h window from the purchase) reads the same way: 7 on
 * the day it started, 0 on the day it ends. A trial whose end has already
 * passed reads null — the caller keeps its "Trial completed" line.
 *
 * The phone's calendar is a fallback. The clock was cut in the user's own zone
 * (users.timezone), so the server's count — GET /api/user/subscription-status
 * `trial_days_left` — wins when it is there (`resolveTrialDaysLeft`); a phone in
 * another zone, or with a skewed clock, could otherwise read 8.
 *
 * Pure, no React: web/src/utils/__tests__/trialDaysLeft.test.ts pins it.
 */

import { localDayNumber, type DayNumberOf } from './calendarDays';

// The day number a count is made of has one home (utils/calendarDays.ts); a trial's
// count reads it through here, as it always has.
export { localDayNumber, type DayNumberOf };

/** The length of a Seven Free Days week (services/trial_clock.py TRIAL_CLOCK_DAYS). */
export const TRIAL_CLOCK_DAYS = 7;

/** The free week's possible lengths (services/trial_clock.py TRIAL_LENGTH_CHOICES). */
export const TRIAL_LENGTH_CHOICES: readonly number[] = [7, 14, 30];

/** A clock trial's length: the server's `trial_length_days` when it is one of the three, else 7. */
export function clockTrialLength(days: number | null | undefined): number {
  return typeof days === 'number' && TRIAL_LENGTH_CHOICES.includes(days) ? days : TRIAL_CLOCK_DAYS;
}

/**
 * Calendar days after today until the trial's last day: 0 on the last day,
 * null once the trial has ended (or the date is unusable). `dayNumberOf` reads
 * the calendar date (the phone's local one by default); the tests inject a
 * zone's, because Jest reads TZ once at startup and a test cannot change it.
 */
export function trialCalendarDaysLeft(
  expiresAt: Date | null | undefined,
  now: Date = new Date(),
  dayNumberOf: DayNumberOf = localDayNumber,
): number | null {
  if (!(expiresAt instanceof Date) || Number.isNaN(expiresAt.getTime())) return null;
  if (expiresAt.getTime() <= now.getTime()) return null;
  return Math.max(0, dayNumberOf(expiresAt) - dayNumberOf(now));
}

/**
 * The days-left number a trial surface shows (review of #3988, 2026-09-26).
 *
 * The server's count wins when it sent one: it is computed in the user's own
 * zone, the one the clock was cut in. Without it (an older backend, a failed
 * field), the phone's calendar count, capped at the week's length for a Seven
 * Free Days clock trial (`clockTrial`): a phone far from home, or with a skewed
 * clock, can otherwise read 8. An ended trial reads null either way.
 */
export function resolveTrialDaysLeft(opts: {
  serverDaysLeft?: number | null;
  expiresAt: Date | null | undefined;
  clockTrial?: boolean;
  /** The free week's length (partner codes build item 2, 2026-09-27): 7, or 14 / 30 when a
   *  partner code lengthened it (subscription-status `trial_length_days`). Caps a clock
   *  trial's count. Anything else reads as 7. */
  clockDays?: number | null;
  now?: Date;
}): number | null {
  const now = opts.now ?? new Date();
  const local = trialCalendarDaysLeft(opts.expiresAt, now);
  const ended =
    opts.expiresAt instanceof Date && !Number.isNaN(opts.expiresAt.getTime()) && opts.expiresAt.getTime() <= now.getTime();
  if (ended) return null;
  const server = opts.serverDaysLeft;
  const days = typeof server === 'number' && Number.isFinite(server) && server >= 0 ? Math.floor(server) : local;
  if (days === null) return null;
  return opts.clockTrial ? Math.min(days, clockTrialLength(opts.clockDays)) : days;
}

/**
 * Account's one-liner for an active trial. The words are the ones Account
 * already used ("Trial • N days left", "<tier> trial • N days left",
 * "Trial completed"); only the number changed, plus the last day — no days
 * after it — which reads "last day".
 */
export function trialActiveOneLiner(tierName: string | null, daysLeft: number | null): string {
  if (daysLeft === null) return 'Trial completed';
  const prefix = tierName ? `${tierName} trial` : 'Trial';
  if (daysLeft === 0) return `${prefix} • last day`;
  return `${prefix} • ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`;
}
